<template>
  <div class="workflow-page">
    <!-- Page Header with Title and View Controls -->
    <div class="page-header py-6 px-10">
      <div class="page-title-section">
        <h1 class="page-title">{{ activeTab === 'my-workflow' ? 'Active Workflows' : 'Popular Workflows' }}</h1>
        <p class="page-subtitle">{{ activeTab === 'my-workflow' ? 'Patient workflows and practice-wide automation currently running' : 'Reusable workflow templates' }}</p>
      </div>
      
      <!-- View Style Controls -->
      <div class="view-controls">
        <button 
          class="view-btn" 
          :class="{ active: viewStyle === 'list' }"
          @click="viewStyle = 'list'"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M18 17C18 17.5523 17.5523 18 17 18H1C0.447716 18 0 17.5523 0 17V14.35C0 13.7977 0.447715 13.35 1 13.35H17C17.5523 13.35 18 13.7977 18 14.35V17ZM18 10.35C18 10.9023 17.5523 11.35 17 11.35H1C0.447716 11.35 0 10.9023 0 10.35V7.625C0 7.07271 0.447715 6.625 1 6.625H17C17.5523 6.625 18 7.07272 18 7.625V10.35ZM18 3.625C18 4.17728 17.5523 4.625 17 4.625H1C0.447716 4.625 0 4.17728 0 3.625V1C0 0.447715 0.447715 0 1 0H17C17.5523 0 18 0.447715 18 1V3.625Z" :fill="viewStyle === 'list' ? '#374957' : '#999999'"/>
          </svg>
        </button>
        <button 
          class="view-btn" 
          :class="{ active: viewStyle === 'grid' }"
          @click="viewStyle = 'grid'"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M11 18C10.4477 18 10 17.5523 10 17V11C10 10.4477 10.4477 10 11 10H17C17.5523 10 18 10.4477 18 11V17C18 17.5523 17.5523 18 17 18H11ZM11 8C10.4477 8 10 7.55228 10 7V1C10 0.447715 10.4477 0 11 0H17C17.5523 0 18 0.447715 18 1V7C18 7.55228 17.5523 8 17 8H11ZM1 8C0.447715 8 0 7.55228 0 7V1C0 0.447715 0.447715 0 1 0H7C7.55228 0 8 0.447715 8 1V7C8 7.55228 7.55228 8 7 8H1ZM1 18C0.447715 18 0 17.5523 0 17V11C0 10.4477 0.447715 10 1 10H7C7.55228 10 8 10.4477 8 11V17C8 17.5523 7.55228 18 7 18H1Z" :fill="viewStyle === 'grid' ? '#374957' : '#999999'"/>
          </svg>
        </button>
        <button 
          class="view-btn" 
          :class="{ active: viewStyle === 'grid-large', disabled: !isGridLargeAvailable }"
          :disabled="!isGridLargeAvailable"
          @click="isGridLargeAvailable && (viewStyle = 'grid-large')"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M1 20C0.447715 20 0 19.5523 0 19V15C0 14.4477 0.447715 14 1 14H5C5.55228 14 6 14.4477 6 15V19C6 19.5523 5.55228 20 5 20H1ZM8 20C7.44772 20 7 19.5523 7 19V15C7 14.4477 7.44772 14 8 14H12C12.5523 14 13 14.4477 13 15V19C13 19.5523 12.5523 20 12 20H8ZM15 20C14.4477 20 14 19.5523 14 19V15C14 14.4477 14.4477 14 15 14H19C19.5523 14 20 14.4477 20 15V19C20 19.5523 19.5523 20 19 20H15ZM1 13C0.447715 13 0 12.5523 0 12V8C0 7.44772 0.447715 7 1 7H5C5.55228 7 6 7.44772 6 8V12C6 12.5523 5.55228 13 5 13H1ZM8 13C7.44772 13 7 12.5523 7 12V8C7 7.44772 7.44772 7 8 7H12C12.5523 7 13 7.44772 13 8V12C13 12.5523 12.5523 13 12 13H8ZM15 13C14.4477 13 14 12.5523 14 12V8C14 7.44772 14.4477 7 15 7H19C19.5523 7 20 7.44772 20 8V12C20 12.5523 19.5523 13 19 13H15ZM1 6C0.447715 6 0 5.55228 0 5V1C0 0.447715 0.447715 0 1 0H5C5.55228 0 6 0.447715 6 1V5C6 5.55228 5.55228 6 5 6H1ZM8 6C7.44772 6 7 5.55228 7 5V1C7 0.447715 7.44772 0 8 0H12C12.5523 0 13 0.447715 13 1V5C13 5.55228 12.5523 6 12 6H8ZM15 6C14.4477 6 14 5.55228 14 5V1C14 0.447715 14.4477 0 15 0H19C19.5523 0 20 0.447715 20 1V5C20 5.55228 19.5523 6 19 6H15Z" :fill="viewStyle === 'grid-large' ? '#374957' : (!isGridLargeAvailable ? '#CCCCCC' : '#999999')"/>
          </svg>
        </button>
      </div>
    </div>

    <div class="px-10 mb-[2px]">
      <Tab
        v-model="activeTab"
        type="outlined"
        :tabs="[
          { value: 'my-workflow', label: 'My Automation', icon: require('@/assets/MyWorkBench/Workflow/my_automation_icon.svg') },
          { value: 'workflow-library', label: 'Automation Library', icon: require('@/assets/MyWorkBench/Workflow/library_icon.svg') }
        ]"
      />
    </div>

    <!-- Content Area with White Background -->
    <div class="content-area -mr-5 -ml-5 px-10">

      <!-- Search Bar and Create Button -->
      <div class="search-header mt-6">
        <div class="search-section">
          <Label label="Search">
            <TextInput
              v-model="searchQuery"
              placeholder="Search workflows"
              size="medium"
              :clearable="true"
            >
              <template #prefix>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z" fill="#374957"/>
                </svg>
              </template>
            </TextInput>
          </Label>
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons">
          <ResponsiveButton
            button_type="filled"
            built_in_theme="dark"
            size="medium"
            display_name="Create Workflow"
            @click-button="createNewWorkflow"
          >
            <template #prefix>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 20 20" fill="none">
                <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM5.07 16.28C5.5 15.38 8.12 14.5 10 14.5C11.88 14.5 14.51 15.38 14.93 16.28C13.57 17.36 11.86 18 10 18C8.14 18 6.43 17.36 5.07 16.28ZM16.36 14.83C14.93 13.09 11.46 12.5 10 12.5C8.54 12.5 5.07 13.09 3.64 14.83C2.62 13.49 2 11.82 2 10C2 5.59 5.59 2 10 2C14.41 2 18 5.59 18 10C18 11.82 17.38 13.49 16.36 14.83ZM10 4C8.06 4 6.5 5.56 6.5 7.5C6.5 9.44 8.06 11 10 11C11.94 11 13.5 9.44 13.5 7.5C13.5 5.56 11.94 4 10 4ZM10 9C9.17 9 8.5 8.33 8.5 7.5C8.5 6.67 9.17 6 10 6C10.83 6 11.5 6.67 11.5 7.5C11.5 8.33 10.83 9 10 9Z" fill="white"/>
              </svg>
            </template>
          </ResponsiveButton>
          <ResponsiveButton
            button_type="filled"
            built_in_theme="light"
            size="medium"
            display_name="Create Program"
            @click-button="createNewProgram"
          >
            <template #prefix>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 22 20" fill="none">
                <path d="M2 20C1.45 20 0.979167 19.8042 0.5875 19.4125C0.195833 19.0208 0 18.55 0 18V4C0 3.45 0.195833 2.97917 0.5875 2.5875C0.979167 2.19583 1.45 2 2 2H10V4H2V18H16V12H18V18C18 18.55 17.8042 19.0208 17.4125 19.4125C17.0208 19.8042 16.55 20 16 20H2ZM4 16H6V9H4V16ZM8 16H10V6H8V16ZM12 16H14V12H12V16ZM17 10C16.5 10 16.0083 9.925 15.525 9.775C15.0417 9.625 14.5917 9.40833 14.175 9.125L13.8 9.475C13.6 9.65833 13.3625 9.75 13.0875 9.75C12.8125 9.75 12.5833 9.65833 12.4 9.475C12.2167 9.29167 12.125 9.05833 12.125 8.775C12.125 8.49167 12.2167 8.25833 12.4 8.075L12.8 7.675C12.5333 7.275 12.3333 6.85 12.2 6.4C12.0667 5.95 12 5.48333 12 5C12 3.61667 12.4875 2.4375 13.4625 1.4625C14.4375 0.4875 15.6167 0 17 0H22V5C22 6.38333 21.5125 7.5625 20.5375 8.5375C19.5625 9.5125 18.3833 10 17 10ZM17 8C17.8333 8 18.5417 7.70833 19.125 7.125C19.7083 6.54167 20 5.83333 20 5V2H17C16.1667 2 15.4583 2.29167 14.875 2.875C14.2917 3.45833 14 4.16667 14 5C14 5.21667 14.0208 5.425 14.0625 5.625C14.1042 5.825 14.1667 6.025 14.25 6.225L16.85 3.625C17.0333 3.44167 17.2667 3.35 17.55 3.35C17.8333 3.35 18.0667 3.44167 18.25 3.625C18.45 3.825 18.55 4.0625 18.55 4.3375C18.55 4.6125 18.45 4.85 18.25 5.05L15.65 7.65C15.8667 7.75 16.0875 7.83333 16.3125 7.9C16.5375 7.96667 16.7667 8 17 8Z" fill="white"/>
              </svg>
            </template>
          </ResponsiveButton>
        </div>
      </div>

      <!-- Automation Type Tabs -->
      <div class="automation-type-tabs">
        <Tab
          v-model="automationTypeTab"
          type="underline"
          :tabs="[
            { value: 'all', label: 'All', icon: require('@/assets/MyWorkBench/Workflow/automation_all.svg') },
            { value: 'workflow', label: 'Workflows', icon: require('@/assets/MyWorkBench/Workflow/automation_workflow.svg') },
            { value: 'program', label: 'Programs', icon: require('@/assets/MyWorkBench/Workflow/automation_program.svg') }
          ]"
        />
      </div>

      <!-- Workflow Grid -->
      <div class="workflow-content mb-10">
        <div v-if="activeTab === 'my-workflow'" class="workflow-grid" :class="`grid-${viewStyle}`">
          <WorkflowCard
            v-for="workflow in paginatedMyWorkflows"
            :key="workflow.id"
            :workflow="workflow"
            :view-style="viewStyle"
            :automation-type="workflow.type"
            @edit="editWorkflow"
            @delete="deleteWorkflow"
          />
        </div>

        <div v-else class="workflow-grid" :class="`grid-${viewStyle}`">
          <WorkflowCard
            v-for="workflow in paginatedLibraryWorkflows"
            :key="workflow.id"
            :workflow="workflow"
            :view-style="viewStyle"
            :is-library="true"
            :automation-type="workflow.type"
            @use="useLibraryWorkflow"
          />
        </div>
      </div>

      <!-- Pagination -->
      <MedicationPagination
        v-if="activeTab === 'my-workflow'"
        :total="filteredMyWorkflows.length"
        :page-size="perPage"
        :current-page="currentPage"
        :page-size-options="[5, 10, 20, 50, 100]"
        @page-change="handlePageChange"
        @page-size-change="handlePageSizeChange"
      />
      <MedicationPagination
        v-else
        :total="filteredLibraryWorkflows.length"
        :page-size="perPage"
        :current-page="currentPage"
        :page-size-options="[5, 10, 20, 50, 100]"
        @page-change="handlePageChange"
        @page-size-change="handlePageSizeChange"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import WorkflowCard from './WorkflowCard.vue';
import MedicationPagination from '@/NewLibrary/MedicationPagination.vue';
import { createWorkflowApi } from '@/api/workflowManage';
import { notify } from '@/utils/notify';

export default {
  name: 'WorkflowList',

  components: {
    WorkflowCard,
    MedicationPagination,
  },
  
  data() {
    return {
      activeTab: 'my-workflow',
      searchQuery: '',
      automationTypeTab: 'all', // 'all', 'workflow', 'program' - for the sub-tabs
      automationFilter: 'all', // 'all', '7days', '30days', '90days' - for the time filter dropdown
      viewStyle: 'grid', // 'list', 'grid', 'grid-large'
      windowWidth: window.innerWidth,
      // Pagination
      currentPage: 1,
      perPage: 10,
    };
  },
  
  computed: {
    ...mapGetters('workbench/workflow', [
      'getMyWorkflows',
      'getWorkflowLibrary',
    ]),
    
    // Check if window is wide enough for 3-column view (minimum ~1400px)
    isGridLargeAvailable() {
      return this.windowWidth >= 1400;
    },
    
    myWorkflowsList: {
      get() {
        // Map is_program from API to type for WorkflowCard component
        return this.getMyWorkflows.map((workflow) => ({
          ...workflow,
          // Use real is_program field from API
          type: workflow.is_program ? 'program' : 'workflow'
        })).sort(
          (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
        );
      }
    },
    
    filteredMyWorkflows() {
      let workflows = this.myWorkflowsList;

      // Filter by automation type tab
      if (this.automationTypeTab !== 'all') {
        workflows = workflows.filter(w => w.type === this.automationTypeTab);
      }

      // Filter by time (automationFilter dropdown)
      if (this.automationFilter !== 'all') {
        const now = new Date();
        const daysMap = {
          '7days': 7,
          '30days': 30,
          '90days': 90
        };
        const days = daysMap[this.automationFilter] || 7;

        workflows = workflows.filter(w => {
          if (!w.updated_at) return false;
          const workflowDate = new Date(w.updated_at);
          const diffDays = (now - workflowDate) / (1000 * 60 * 60 * 24);
          return diffDays <= days;
        });
      }

      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        workflows = workflows.filter(w =>
          w.name.toLowerCase().includes(query) ||
          (w.description && w.description.toLowerCase().includes(query))
        );
      }

      return workflows;
    },
    
    filteredLibraryWorkflows() {
      const workflows = this.getWorkflowLibrary.map((workflow) => ({
        ...workflow,
        // Use real is_program field from API
        type: workflow.is_program ? 'program' : 'workflow'
      }));
      if (!this.searchQuery) return workflows;
      const query = this.searchQuery.toLowerCase();
      return workflows.filter(w =>
        w.name.toLowerCase().includes(query) ||
        w.description.toLowerCase().includes(query) ||
        w.category.toLowerCase().includes(query)
      );
    },
    
    paginatedMyWorkflows() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredMyWorkflows.slice(start, end);
    },

    paginatedLibraryWorkflows() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredLibraryWorkflows.slice(start, end);
    },
  },

  watch: {
    // Reset to page 1 when filters change
    automationTypeTab() {
      this.currentPage = 1;
    },
    searchQuery() {
      this.currentPage = 1;
    },
    activeTab() {
      this.currentPage = 1;
    },
  },
  
  async mounted() {
    window.addEventListener('resize', this.handleResize);
    
    // Initialize workflows from API
    try {
      await this.initializeWorkflows();
    } catch (error) {
      console.error('Failed to initialize workflows:', error);
      notify({
        title: 'Error',
        message: 'Failed to load workflows. Please refresh the page.',
        type: 'error',
        showClose: true
      });
    }
  },
  
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
  
  methods: {
    ...mapActions('workbench/workflow', [
      'initializeWorkflows',
      'addMyWorkflow',
      'reorderMyWorkflows',
      'deleteMyWorkflow',
      'setActiveWorkflow',
      'copyFromLibrary',
    ]),
    
    
    editWorkflow(workflowId) {
      this.setActiveWorkflow(workflowId);
      this.$router.push(`/my-workbench/automation/edit/${workflowId}`);
    },
    
    deleteWorkflow(workflowId) {
      // Find the workflow to check its status and assigned patients
      const workflow = this.myWorkflowsList.find(w => w.id === workflowId);
      
      if (!workflow) {
        notify({
          title: 'Error',
          message: 'Workflow not found',
          type: 'error',
          showClose: true
        });
        return;
      }
      
      // Check if workflow is active and has assigned patients
      const isActive = workflow.status === 'active';
      const hasAssignedPatients = (workflow.assigned_patients_count || 0) >= 1;
      
      if (isActive && hasAssignedPatients) {
        notify({
          title: 'Cannot Archive Workflow',
          message: `This workflow is active and has ${workflow.assigned_patients_count} assigned patient(s). Please remove assigned patients before archiving.`,
          type: 'warning',
          duration: 5000,
        });
        return;
      }
      
      // Proceed with archive if validation passes
      this.deleteMyWorkflow(workflowId);
    },
    
    useLibraryWorkflow(libraryWorkflowId) {
      const newWorkflowId = `create_${Math.floor(Math.random() * 1000000)}`;
      this.copyFromLibrary({ libraryWorkflowId, newWorkflowId });
      this.$router.push(`/my-workbench/automation/edit/${newWorkflowId}`);
    },
    
    async createNewWorkflow() {
      try {
        console.log('Creating new draft workflow...');
        
        // Call API to create a draft empty workflow
        const response = await createWorkflowApi({
          name: 'Untitled Workflow ' + Math.floor(Math.random() * 10000000),
          description: '',
          type: 'patient',
          definition: {
            nodes: [],
            connections: [],
            metadata: {}
          }
        });
        
        console.log('Draft workflow created successfully:', response);
        console.log('Workflow ID:', response.data?.id);
        
        const newWorkflow = response.data;
        
        if (!newWorkflow || !newWorkflow.id) {
          throw new Error('Invalid workflow response from API');
        }
        
        // Add new workflow to the beginning of the store list
        this.addMyWorkflow(newWorkflow);
        
        // Navigate to workflow canvas with the new workflow ID
        this.$router.push(`/my-workbench/automation/edit/${newWorkflow.id}`);
        
        notify({
          title: 'Success',
          message: 'New workflow created successfully!',
          type: 'success',
          showClose: true
        });
      } catch (error) {
        console.error('Error creating draft workflow:', error);
        console.error('Error details:', error.response?.data || error.message);
        notify({
          title: 'Error',
          message: `Failed to create workflow: ${error.response?.data?.message || error.message}`,
          type: 'error',
          showClose: true
        });
      }
    },

    async createNewProgram() {
      try {
        // Import API function
        const { createWorkflowApi } = await import('@/api/workflowManage');

        // Create a new program with required program fields
        const response = await createWorkflowApi({
          name: 'Untitled Program ' + Math.floor(Math.random() * 10000000),
          description: '',
          type: 'patient',
          is_program: true,
          duration: {
            type: 'rolling',
            unit: 'days',
            value: 30
          },
          price: 0,
          definition: {
            nodes: [],
            connections: [],
            metadata: {}
          }
        });

        console.log('Draft program created successfully:', response);
        console.log('Program ID:', response.data?.id);

        const newProgram = response.data;

        if (!newProgram || !newProgram.id) {
          throw new Error('Invalid program response from API');
        }

        // Add new program to the beginning of the store list with type 'program'
        this.addMyWorkflow({
          ...newProgram,
          type: 'program'
        });

        // Navigate to workflow canvas with the new program ID
        this.$router.push(`/my-workbench/automation/edit/${newProgram.id}`);

        notify({
          title: 'Success',
          message: 'New program created successfully!',
          type: 'success',
          showClose: true
        });
      } catch (error) {
        console.error('Error creating draft program:', error);
        console.error('Error details:', error.response?.data || error.message);
        notify({
          title: 'Error',
          message: `Failed to create program: ${error.response?.data?.message || error.message}`,
          type: 'error',
          showClose: true
        });
      }
    },

    handleResize() {
      this.windowWidth = window.innerWidth;
      // If window becomes too narrow and grid-large is selected, switch to grid
      if (!this.isGridLargeAvailable && this.viewStyle === 'grid-large') {
        this.viewStyle = 'grid';
      }
    },

    handlePageChange(page) {
      this.currentPage = page;
    },

    handlePageSizeChange(size) {
      this.perPage = size;
      this.currentPage = 1; // Reset to first page when page size changes
    },
  },
};
</script>

<style scoped>
.action-buttons :deep(.prefix-wrapper) {
  padding: 2px;
}
.workflow-page {
  padding: 0;
  background: transparent;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Page Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  background: transparent;
}

.page-title-section {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.page-title {
  font-family: 'Avenir', sans-serif;
  font-size: 22px;
  font-weight: 500;
  line-height: 24px;
  color: #333;
  margin: 0;
  white-space: nowrap;
}

.page-subtitle {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.5px;
  color: #666;
  margin: 0;
}

/* View Controls */
.view-controls {
  display: flex;
  gap: 8px;
}

.view-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid #E5E7EB;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
}

.view-btn:hover:not(.disabled) {
  background: #F3F4F6;
  border-color: #004897;
}

.view-btn.active {
  background: #E3F2FD;
  border-color: #004897;
}

.view-btn.active svg path {
  fill: #374957;
}

.view-btn.disabled {
  cursor: not-allowed;
  opacity: 0.4;
  background: #F9F9F9;
}

.view-btn.disabled:hover {
  border-color: #E5E7EB;
}

/* Content Area */
.content-area {
  background: #FFFFFF;
  margin: 0;
  box-shadow: 0px 1px 15px 0px rgba(164, 164, 164, 0.25);
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

/* Search Header */
.search-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  padding-bottom: 16px;
  border-bottom: 1px solid #E0E0E0;
}

/* Search Section */
.search-section {
  display: flex;
  gap: 12px;
  align-items: center;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 12px;
  align-items: center;
}

/* Automation Type Tabs */
.automation-type-tabs {
  padding: 16px 12px 0 12px;
  background: #FFFFFF;
}

.automation-type-tabs :deep(.va-tab-group__main) {
  display: flex;
  width: 100%;
}

.automation-type-tabs :deep(.va-tab) {
  flex: 1;
  min-width: 0;
  justify-content: center;
}

/* Workflow Content */
.workflow-content {
  padding: 24px 12px;
  flex: 1;
  overflow-y: auto;
}

/* Workflow Grid */
.workflow-grid {
  display: grid;
  gap: 12px;
}

.workflow-grid.grid-list {
  grid-template-columns: 1fr;
}

.workflow-grid.grid-grid {
  grid-template-columns: repeat(2, 1fr);
}

.workflow-grid.grid-grid-large {
  grid-template-columns: repeat(3, 1fr);
}

/* Create New Workflow Card */
.create-workflow-card {
  background: #F2F2F2;
  border: 1px dashed #C4C4C4;
  border-radius: 10px;
  padding: 24px;
  height: 144px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.create-workflow-card:hover {
  background: #E8E8E8;
  border-color: #999;
}

.create-icon-wrapper {
  width: 56px;
  height: 56px;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f9fbfd;
  border-radius: 5px;
  flex-shrink: 0;
}

.create-text {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #333;
  margin: 0;
}
</style>
